// German for the standing brief. Keys already carried by i18n/common.de.ts (Confirm, Archive,
// Saving…, Loading…, held, us, them, late, due, current, not collected, Unheld …) are not
// repeated: the first registration wins and a second one with a different word warns.
//
// The state words (cooling, warm, quiet) and `ball` are also registered by PeoplePage with the
// SAME German — that is deliberate and silent; only a disagreement would be reported.
import { registerDe } from '../i18n/index.js';

registerDe({
  // ── addressing ──
  'no person was addressed': 'es wurde keine Person adressiert',
  'This address names no person': 'Diese Adresse nennt keine Person',
  'A person brief is opened as #person?id=N from a row on the People board.':
    'Ein Personendossier wird als #person?id=N aus einer Zeile der Personenübersicht geöffnet.',
  'reading the record…': 'der Bestand wird gelesen…',
  'the record did not answer': 'der Bestand hat nicht geantwortet',
  'Could not read this person': 'Diese Person konnte nicht gelesen werden',

  // ── the masthead ──
  'role not named': 'Funktion nicht benannt',
  'no current affiliation on the record': 'keine aktuelle Zugehörigkeit erfasst',
  'register not set': 'Anrede nicht gesetzt',
  // the mast's buying-influence tag beside the tier (R7 §6.1) — ProfileZone's word, identical
  'estimated': 'geschätzt',
  'tier': 'Stufe',
  'cadence': 'Taktung',
  '{n} unconfirmed': '{n} unbestätigt',
  'do not contact': 'nicht kontaktieren',
  'Add fact': 'Sachverhalt hinzufügen',
  // 'Research' alone is the NOUN of the fourth column below; the mast's verb carries its own
  // context key so the two never overwrite each other.
  'Research‖verb': 'Recherchieren',
  'Queuing…': 'Wird eingereiht…',
  'Send an agent to read the web for this person: the employer and the role from the organisation’s own pages and the press, never LinkedIn. Everything arrives in Review as a proposal.':
    'Einen Agenten das Web zu dieser Person lesen lassen: Arbeitgeber und Funktion von den eigenen Seiten der Organisation und aus der Presse, nie von LinkedIn. Alles erscheint in der Prüfung als Vorschlag.',
  'Research {ref} queued. What it can cite arrives in Review as a proposal; nothing is written until you hold it.':
    'Recherche {ref} eingereiht. Was sie belegen kann, erscheint in der Prüfung als Vorschlag; geschrieben wird erst, wenn Sie es übernehmen.',

  // ── the rail ──
  'Day': 'Tag',
  'since last touch': 'seit der letzten Berührung',
  'Ball': 'Ball',
  'was due': 'war fällig',
  'Threads': 'Fäden',
  'mails': 'Mails',
  '{n} mail': '{n} Mail', '{n} mails': '{n} Mails',
  'the mail service did not answer': 'der Maildienst hat nicht geantwortet',
  'counting the correspondence…': 'die Korrespondenz wird gezählt…',
  'no mail with this person on record': 'keine Mail mit dieser Person erfasst',
  '{n} from them': '{n} von ihr', '{n} from you': '{n} von Ihnen',
  'their median reply {h} h': 'ihre mittlere Antwortzeit {h} h',
  'Sources': 'Quellen',
  '{n} fact with evidence': '{n} belegter Sachverhalt',
  '{n} facts with evidence': '{n} belegte Sachverhalte',
  '{n} interaction': '{n} Kontakt', '{n} interactions': '{n} Kontakte',
  '{n} fact': '{n} Sachverhalt', '{n} facts': '{n} Sachverhalte',
  '{n} affiliation': '{n} Zugehörigkeit', '{n} affiliations': '{n} Zugehörigkeiten',
  'nobody has held them': 'niemand hat sie übernommen',
  'every claim on this page is held': 'jede Aussage auf dieser Seite ist übernommen',
  'nothing has been logged against them': 'zu dieser Person wurde nichts erfasst',
  '{us} with us · {them} with them': '{us} bei uns · {them} bei ihnen',
  'no open commitment either way': 'keine offene Zusage in beide Richtungen',

  // ── next move ──
  'Next move': 'Nächster Zug',
  'ranked · the page arranges, it does not decide':
    'geordnet · die Seite ordnet an, sie entscheidet nicht',
  'ball': 'Ball',
  'Nothing is owed either way. A move appears here when a commitment is logged against this person — from a meeting, a mail or by hand — so the next thing to do is always something somebody actually said.':
    'In beide Richtungen ist nichts offen. Ein Zug erscheint hier, sobald zu dieser Person eine Zusage erfasst wird — aus einem Treffen, einer Mail oder von Hand —, damit das Nächste immer etwas ist, das jemand wirklich gesagt hat.',


  // ── the writing profile ──
  'How you two write': 'Wie Sie beide schreiben',
  'the writing profile drives every draft to them': 'das Schreibprofil steuert jeden Entwurf an sie',

  // ── the timeline ──
  'Latest': 'Zuletzt',
  'newest {n} of {total}': 'die neuesten {n} von {total}',
  'No interaction names this person. A meeting, a mail or a note becomes an interaction with them as a participant; the seed import files the two research meetings that way.':
    'Kein Kontakt nennt diese Person. Ein Treffen, eine Mail oder eine Notiz wird zu einem Kontakt mit ihr als Teilnehmerin; der Seed-Import legt die beiden Recherchetreffen genau so ab.',

  // ── the record ──
  'What we currently know': 'Was wir derzeit wissen',
  'Show everything': 'Alles zeigen',
  'Only unconfirmed ({n})': 'Nur unbestätigte ({n})',
  'nothing unconfirmed here': 'hier ist nichts unbestätigt',
  'unconfirmed': 'unbestätigt',
  'no value': 'kein Wert',
  '{key} is held.': '{key} ist übernommen.',
  '{org} is held.': '{org} ist übernommen.',
  'since': 'seit',

  // ── the four columns ──
  'Identity': 'Identität',
  'Affiliations': 'Zugehörigkeiten',
  'Relationship': 'Beziehung',
  'Research': 'Recherche',
  'former': 'früher',
  'retracted': 'zurückgezogen',

  // ── the vocabularies a row prints ──
  'mail_in': 'Mail ein',
  'mail_out': 'Mail aus',
  // 'meeting', 'interaction' and 'Evidence' are ReviewPage's words (Termin, Kontaktpunkt,
  // Belege) — registered there, not repeated here.
  'call': 'Anruf',
  'note': 'Notiz',
  'post': 'Beitrag',
  'signal': 'Signal',
  'operator': 'Operatorin',
  'agent': 'Agent',
  'import': 'Import',
  'official': 'amtlich',
  'professional': 'beruflich',
  'meeting_label': 'Treffen-Etikett',
  'mail_signature': 'Mailsignatur',
  'snapshot': 'Snapshot',
  'surfe': 'Surfe',
  'manual': 'manuell',
  'url': 'URL',
  'document': 'Dokument',

  // ── the states, shared with the board ──
  'cooling': 'kühlt ab',
  'warm': 'warm',
  'quiet': 'still',

  // ── the two dialogues ──
  'Write it': 'Schreiben',
  'Key': 'Schlüssel',
  'Value': 'Wert',
  'Evidence reference': 'Belegreferenz',
  'a URL, a snapshot id, an interaction ref': 'eine URL, eine Snapshot-Id, eine Kontaktreferenz',
  'A fact needs a key, for example identity.email.':
    'Ein Sachverhalt braucht einen Schlüssel, zum Beispiel identity.email.',
  '{key} written. Whatever the key said before is superseded, not erased.':
    '{key} geschrieben. Was der Schlüssel vorher sagte, ist abgelöst, nicht gelöscht.',
  'Writing a key that already has a value supersedes the old one. Nothing is overwritten and nothing is lost.':
    'Einen Schlüssel erneut zu schreiben löst den alten Wert ab. Nichts wird überschrieben und nichts geht verloren.',
  'Archive {name}?': '{name} archivieren?',
  'They leave the register and the board. Every fact, affiliation, interaction and commitment stays exactly where it is, and the archive can be searched with status=archived.':
    'Sie verlässt das Verzeichnis und die Übersicht. Jeder Sachverhalt, jede Zugehörigkeit, jeder Kontakt und jede Zusage bleibt genau dort, wo sie ist; das Archiv lässt sich mit status=archived durchsuchen.',
  '{name} is archived. The record keeps everything.':
    '{name} ist archiviert. Der Bestand behält alles.',

  // ── round 3b: where this person came from ──
  'Origin': 'Herkunft',
  'the first thing the record knows about this person':
    'das Erste, was das Dossier über diese Person weiss',
  'email': 'E-Mail',
  'phone': 'Telefon',
  'location': 'Ort',
  'Nothing has passed between you and this person yet. The next move, the reason to act and the writing profile appear here once something does.': 'Zwischen Ihnen und dieser Person ist noch nichts gelaufen. Der nächste Zug, der Grund zu handeln und das Schreibprofil erscheinen hier, sobald etwas passiert.',
}, 'PersonPage');
